import { gql, useMutation } from '@apollo/client';
import {
    Accordion,
    AccordionActions,
    AccordionDetails,
    AccordionSummary,
    Box,
    Checkbox,
    FormControlLabel,
    FormGroup,
    MenuItem,
    Select,
    Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import classNames from 'classnames';
import { useEffect, useState } from 'react';
import { DATASET_DRY_CARGO, DATASET_TANKERS } from '_legacy/models/Datasets';
import _ from 'lodash';
import tankersGroupTypes, {
    getTankersGroupTypeByValue,
} from '_legacy/models/TankersGroupTypes';
import store from 'store';
import { refreshUserInfo } from 'store/feature/userSlice';

// Define mutation
const UpdateGroupSettingsMutation = gql`
    mutation UpdateGroupSettings(
        $id: ID!
        $groupOnlyExpiryPeriod: Int!
        $groupSettings: GroupSettingsInput!
    ) {
        updateGroupSettings(
            id: $id
            groupOnlyExpiryPeriod: $groupOnlyExpiryPeriod
            groupSettings: $groupSettings
        ) {
            id
            groupOnlyExpiryPeriod
            groupSettings {
                useDeliveryZoneForAreaMapping
                useDeliveryZoneForAreaGearedMapping
                useDeliveryZoneForAreaGearlessMapping
                useDeliveryZoneForDirectionMapping
                useHyphenSeparatorForLocations
                tankersGroupType
            }
        }
    }
`;

export interface IGroupSettings {
    useDeliveryZoneForAreaMapping: boolean;
    useDeliveryZoneForAreaGearedMapping: boolean;
    useDeliveryZoneForAreaGearlessMapping: boolean;
    useDeliveryZoneForDirectionMapping: boolean;
    useHyphenSeparatorForLocations: boolean;
    tankersGroupType?: string;
}

interface IGroupSettingsProps {
    handleGroupSettingsChanged: () => void;
    group: {
        id: string;
        groupOnlyExpiryPeriod: number;
        groupSettings: IGroupSettings;
        dataset: { id: number };
    };
}

export function GroupSettings(props: IGroupSettingsProps) {
    const isDryGroup = props.group.dataset.id === DATASET_DRY_CARGO;
    const isTankersGroup = props.group.dataset.id === DATASET_TANKERS;
    const [mutateFunction, { loading }] = useMutation(
        UpdateGroupSettingsMutation
    );

    const [groupOnlyExpiryPeriod, setGroupOnlyExpiryPeriod] = useState<number>(
        props.group.groupOnlyExpiryPeriod
    );

    const initialGroupSettings: IGroupSettings = {
        useDeliveryZoneForAreaMapping:
            props.group.groupSettings.useDeliveryZoneForAreaMapping,
        useDeliveryZoneForAreaGearedMapping:
            props.group.groupSettings.useDeliveryZoneForAreaGearedMapping,
        useDeliveryZoneForAreaGearlessMapping:
            props.group.groupSettings.useDeliveryZoneForAreaGearlessMapping,
        useDeliveryZoneForDirectionMapping:
            props.group.groupSettings.useDeliveryZoneForDirectionMapping,
        useHyphenSeparatorForLocations:
            props.group.groupSettings.useHyphenSeparatorForLocations,
        tankersGroupType: props.group.groupSettings.tankersGroupType,
    };

    const [groupSettings, setGroupSettings] =
        useState<IGroupSettings>(initialGroupSettings);

    const [saveEnabled, setSaveEnabled] = useState<boolean>(false);

    const handleGroupOnlyExpiryPeriodChange = (e) => {
        setGroupOnlyExpiryPeriod(Number.parseInt(e.target.value));
    };

    const handleGroupOnlyExpiryPeriodKeyDown = (e) => {
        const ignoredKeys = ['.', 'e', 'E', '-', '+'];

        if (ignoredKeys.find((key) => key === e.key)) {
            e.preventDefault();
        }
    };

    useEffect(() => {
        const isSaveEnabled =
            groupOnlyExpiryPeriod !== props.group.groupOnlyExpiryPeriod ||
            !_.isEqual(groupSettings, initialGroupSettings);
        setSaveEnabled(isSaveEnabled);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        groupOnlyExpiryPeriod,
        groupSettings,
        props.group.groupOnlyExpiryPeriod,
    ]);

    return (
        <Accordion>
            <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                id="group-setings"
            >
                <Typography component="label">Group settings</Typography>
            </AccordionSummary>
            <AccordionDetails>
                <FormGroup>
                    <label htmlFor="groupOnlyExpiryPeriodInput">
                        Group Only Expiry Period
                        <input
                            id="groupOnlyExpiryPeriodInput"
                            className="group-only-expiry-period-input"
                            type="number"
                            min="0"
                            onKeyDown={handleGroupOnlyExpiryPeriodKeyDown}
                            onChange={handleGroupOnlyExpiryPeriodChange}
                            defaultValue={props.group.groupOnlyExpiryPeriod}
                        />
                        hours
                    </label>
                    {isTankersGroup && (
                        <>
                            <Typography
                                component="label"
                                fontFamily={'Cera GR'}
                                marginTop={3}
                            >
                                Tankers Group Type
                            </Typography>
                            <Select
                                value={groupSettings.tankersGroupType}
                                onChange={(e) =>
                                    setGroupSettings({
                                        ...groupSettings,
                                        tankersGroupType: e.target
                                            .value as string,
                                    })
                                }
                                displayEmpty
                                sx={{
                                    marginTop: 1,
                                    marginBottom: 2,
                                    width: '300px',
                                }}
                            >
                                <MenuItem value={tankersGroupTypes[0].value}>
                                    {tankersGroupTypes[0].value}
                                </MenuItem>
                                <MenuItem value={tankersGroupTypes[1].value}>
                                    {tankersGroupTypes[1].value}
                                </MenuItem>
                            </Select>
                        </>
                    )}
                    <Typography
                        component="label"
                        fontFamily={'Cera GR'}
                        marginTop={3}
                    >
                        Use Delivery zone if Load zone is empty:
                    </Typography>
                    <Box
                        marginLeft={3}
                        display="flex"
                        flexDirection="column"
                        alignItems="start"
                    >
                        {isDryGroup ? (
                            <>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={
                                                groupSettings.useDeliveryZoneForAreaGearlessMapping
                                            }
                                            onChange={(_event, checked) =>
                                                setGroupSettings({
                                                    ...groupSettings,
                                                    useDeliveryZoneForAreaGearlessMapping:
                                                        checked,
                                                })
                                            }
                                            color="primary"
                                            disableRipple
                                        />
                                    }
                                    label="Area Gearless mapping"
                                    labelPlacement="start"
                                    disableTypography
                                />
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={
                                                groupSettings.useDeliveryZoneForAreaGearedMapping
                                            }
                                            onChange={(_event, checked) =>
                                                setGroupSettings({
                                                    ...groupSettings,
                                                    useDeliveryZoneForAreaGearedMapping:
                                                        checked,
                                                })
                                            }
                                            color="primary"
                                            disableRipple
                                        />
                                    }
                                    label="Area Geared mapping"
                                    labelPlacement="start"
                                    disableTypography
                                />
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={
                                                groupSettings.useDeliveryZoneForDirectionMapping
                                            }
                                            onChange={(_event, checked) =>
                                                setGroupSettings({
                                                    ...groupSettings,
                                                    useDeliveryZoneForDirectionMapping:
                                                        checked,
                                                })
                                            }
                                            color="primary"
                                            disableRipple
                                        />
                                    }
                                    label="Direction mapping"
                                    labelPlacement="start"
                                    disableTypography
                                />
                            </>
                        ) : (
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={
                                            groupSettings.useDeliveryZoneForAreaMapping
                                        }
                                        onChange={(_event, checked) =>
                                            setGroupSettings({
                                                ...groupSettings,
                                                useDeliveryZoneForAreaMapping:
                                                    checked,
                                            })
                                        }
                                        color="primary"
                                        disableRipple
                                    />
                                }
                                label="Area mapping"
                                labelPlacement="start"
                                disableTypography
                            />
                        )}
                    </Box>
                    <Typography component="label" variant="h5">
                        All historical rows will not be affected; you need to
                        raise a migration ticket.
                    </Typography>
                    <Typography
                        component="label"
                        fontFamily={'Cera GR'}
                        marginTop={3}
                    >
                        Location/zones/rates display:
                    </Typography>
                    <Box
                        marginLeft={3}
                        display="flex"
                        flexDirection="column"
                        alignItems="start"
                    >
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={
                                        groupSettings.useHyphenSeparatorForLocations
                                    }
                                    onChange={(_event, checked) =>
                                        setGroupSettings({
                                            ...groupSettings,
                                            useHyphenSeparatorForLocations:
                                                checked,
                                        })
                                    }
                                    color="primary"
                                    disableRipple
                                />
                            }
                            label="Use hyphen separator for multiple locations/zones/rates"
                            labelPlacement="start"
                            disableTypography
                        />
                    </Box>
                </FormGroup>
            </AccordionDetails>
            <AccordionActions>
                <button
                    className={classNames('ui primary button save', {
                        loading: loading,
                    })}
                    disabled={!saveEnabled}
                    onClick={() => {
                        mutateFunction({
                            variables: {
                                id: props.group.id,
                                groupOnlyExpiryPeriod: groupOnlyExpiryPeriod,
                                groupSettings: {
                                    ...groupSettings,
                                    tankersGroupType:
                                        getTankersGroupTypeByValue(
                                            groupSettings.tankersGroupType
                                        )?.id,
                                },
                            },
                            onCompleted: (data) => {
                                if (data.updateGroupSettings) {
                                    store.dispatch(refreshUserInfo());
                                    props.handleGroupSettingsChanged();
                                }
                            },
                            context: {
                                errorHandling: {
                                    unknownResult: {
                                        resolution: { isSafeToRetry: true },
                                    },
                                },
                            },
                        });
                    }}
                >
                    Save
                </button>
            </AccordionActions>
        </Accordion>
    );
}
